'use client';

import React, { useState, useMemo } from 'react';
import { products, categories, type Category } from '@/lib/product-data';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export default function ProductsPage() {
  const [selectedCategory, setSelectedCategory] =
    useState<Category>('All Products');

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'All Products') {
      return products;
    }
    return products.filter((product) => product.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <div className="bg-background">
      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primary">
            Our Products
          </h1>
          <p className="mt-2 text-lg text-muted-foreground">
            A curated selection of enterprise-grade IT hardware.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          <aside className="w-full md:w-1/4 lg:w-1/5">
            <div className="sticky top-24">
              <h2 className="text-xl font-semibold mb-4 text-primary">
                Categories
              </h2>
              <div className="flex flex-col space-y-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant="ghost"
                    className={cn(
                      'justify-start',
                      selectedCategory === category &&
                        'bg-accent text-accent-foreground'
                    )}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </aside>

          <main className="w-full md:w-3/4 lg:w-4/5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <Card
                  key={product.id}
                  className={cn(
                    'flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:-translate-y-1',
                    product.cssClass
                  )}
                >
                  <CardHeader>
                    <div className="flex justify-center items-center mb-4 text-primary">
                      <product.icon className="w-12 h-12" />
                    </div>
                    <CardTitle className="text-lg font-bold text-center text-primary">
                      {product.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground text-center">
                      {product.category}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {filteredProducts.length === 0 && (
              <div className="text-center py-16 text-muted-foreground">
                <p>No products found in this category.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
